// ChatDashboard.js
import React, { useState, useRef, useEffect, useCallback, useContext } from "react";
import './Dashboard.css';
import '../App.css';
import { log } from "../utils/logger.js";
import ChatContainer from './ChatContainer.js'
import JSZip from 'jszip';
import { setCookie, getCookie, removeCookie} from '../cookieUtils.js';
import { debounce, set } from 'lodash';
import AddToVaultSection from "../components/AddToVault.js";
import VaultSelector from "../components/VaultSelector.js";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { AuthContext }  from '../App.js'

// Set the worker source to the locally hosted `pdf.worker.mjs`
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.mjs";


function Dashboard({ Ampersandposium, setAmpersandposium, userEmail, styles, isDarkMode, wait, isMobile, displayValue, setDisplayValue, vaults = ['-'], setVaults = null, refreshVaults = null }) {
  const { vectorVault } = useContext(AuthContext);
  const ChatContainerRef = useRef(null); // create a reference using the useRef hook
  const [notificationMessage, setNotificationMessage] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [customDataVisible, setCustomDataVisible] = useState(false);
  const [customData, setCustomData] = useState('');
  const [showDelete, setShowDelete] = useState(true);
  const [showVault, setShowVault] = useState(true);
  const [showPersonality, setShowPersonality] = useState(true);
  const [display, setDisplay] = useState(true);
  const [database, setDatabase] = useState(true);
  const [reloadData, setReloadData] = useState(false);
  const [personality, setPersonality] = useState('');

  useEffect(() => {
    if (Ampersandposium?.startsWith('Flow: ')) {
      setShowPersonality(false);
      setShowVault(false);
    } else {
      setShowPersonality(true);
      setShowVault(true);
    }
  }, [Ampersandposium]); // Only run when Ampersandposium changes

  // Refresh vaults every time the dashboard is visited
  useEffect(() => {
    if (refreshVaults && !wait && userEmail) {
      refreshVaults();
    }
  }, [refreshVaults, wait, userEmail]);




  // for calculating how many lines to show on the personality section
  const calculateRows = (text) => {
    if (!text) return 2;
    const charsPerLine = isMobile ? 50 : 100; // Fewer chars per line on mobile
    const lines = text.split('\n');
    
    return lines.reduce((total, line) => {
      return total + Math.ceil(line.length / charsPerLine);
    }, 0);
  };





  
  // Handle window resize
  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (reloadData && !wait && Ampersandposium) {
      updateAmpersandposium(Ampersandposium);
    }
  }, [reloadData, wait, Ampersandposium]);

  useEffect(() => {
    if (!wait && Ampersandposium) {
      fetchPersonality()
    }
  }, [reloadData, wait, Ampersandposium]);

  // Updated debounced updateAmpersandposium
  const updateAmpersandposium = useCallback(
    debounce((value) => {
      setAmpersandposium(value);
      setCookie("Ampersandposium", value);
      setReloadData(prev => !prev);
    }, 300),
    []
  );
  
  const Notification = ({ message }) => {
    // Effect to automatically hide the notification after 5 seconds
    useEffect(() => {
      let timeoutId;
      if ( notificationMessage ) {
        // Set a timer to hide the notification
        timeoutId = setTimeout(() => {
          setNotificationMessage('')
        }, 5000);
      }
      
      // Clean up the timer
      return () => clearTimeout(timeoutId);
    }, [notificationMessage]);

    // Don't render the notification if it's not supposed to be shown
    if (!notificationMessage) {
        return null;
    }

    // Render the notification
    return (
      <div style={styles.notificationStyle}>
        {message}
      </div>
    );
  };


  async function handleFileUpload(event) {
    const file = event.target.files[0];
    if (!file) return; // No file selected
    
    log('file type:', file.type);
    setIsLoading(true);

    try {
        let uploadResult;
        
        switch(file.type) {
            case 'text/plain': // .txt
            case 'text/csv':   // .csv
            case 'text/markdown': // .md
                const textReader = new FileReader();
                uploadResult = await new Promise((resolve, reject) => {
                    textReader.onload = async (e) => {
                        try {
                            const response = await vectorVault.addCloud({ 
                                vault: Ampersandposium, 
                                text: e.target.result, 
                                name: file.name,
                                split: true,
                                split_size: 1000
                            });
                            resolve(response);
                        } catch (error) {
                            reject(error);
                        }
                    };
                    textReader.onerror = () => reject(new Error('Failed to read file'));
                    textReader.readAsText(file);
                });
                break;
                
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': // .docx
                const docxReader = new FileReader();
                uploadResult = await new Promise((resolve, reject) => {
                    docxReader.onload = async (e) => {
                        try {
                            const fileContent = await extractTextFromDocx(e.target.result);
                            const response = await vectorVault.addCloud({ 
                                vault: Ampersandposium, 
                                text: fileContent, 
                                name: file.name,
                                split: true,
                                split_size: 1000
                            });
                            resolve(response);
                        } catch (error) {
                            reject(error);
                        }
                    };
                    docxReader.onerror = () => reject(new Error('Failed to read file'));
                    docxReader.readAsArrayBuffer(file);
                });
                break;
                
            case 'application/pdf': // PDF case
                uploadResult = await vectorVault.uploadPdf(
                    file, 
                    Ampersandposium,
                    { 
                        summarize: true,
                    }
                );
                break;
                
            default:
                setIsLoading(false);
                alert("Unsupported file type.");
                return;
        }
        
        log('Upload result:', uploadResult);
        
        // Display success message
        if (file.type === 'application/pdf') {
            alert(`PDF processed successfully. Added ${uploadResult.items_added || 'multiple'} items to your vault.`);
        } else if (file.type.startsWith('image/')) {
            alert("Text successfully extracted.");
        } else {
            alert("File uploaded successfully.");
        }
        
    } catch (error) {
        console.error("Error uploading file:", error);
        alert(`Error uploading file: ${error.message || 'Unknown error'}`);
    } finally {
        event.target.value = null; // Reset the file input
        setIsLoading(false); // Hide spinner
        setReloadData(prevState => !prevState); // Reload data
    }
  }
  

  async function extractTextFromDocx(arrayBuffer) {
      const zip = new JSZip();
      const content = await zip.loadAsync(arrayBuffer);
      const docXML = await content.files["word/document.xml"].async("text");
      const parser = new DOMParser();
      const docNode = parser.parseFromString(docXML, "application/xml");

      const nsResolver = (prefix) => {
          return {
              'w': 'http://schemas.openxmlformats.org/wordprocessingml/2006/main'
          }[prefix] || null;
      };

      const textNodeResults = docNode.evaluate('//w:t', docNode, nsResolver, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null);

      let textContentArray = [];
      for (let i = 0; i < textNodeResults.snapshotLength; i++) {
          textContentArray.push(textNodeResults.snapshotItem(i).textContent);
      }
      
      return textContentArray.join(" ");
  }
  
  async function handleCustomDataSave(customDataContent) {
      setIsLoading(true); 
      await vectorVault.addCloud({ vault: Ampersandposium, text : customDataContent })
      .catch(error => {
          console.error("There was an error adding your data to the cloud:", error);
      })
      .finally(() => {
          setIsLoading(false); // Hide spinner when the fetch is complete
          setCustomData('')
          setReloadData(prevState => !prevState); // Reload data
          alert("Data uploaded successfully.")
      });
  }



  async function handlePersonalitySave(personalityMessage) {
      setIsLoading(true); 
      setPersonality(personalityMessage)
      await vectorVault.savePersonalityMessage(Ampersandposium, personalityMessage)
      alert("Personality saved successfully.") 
      setIsLoading(false); 
  }

  const fetchPersonality = useCallback(async () => {
    try {
      const message = await vectorVault.fetchPersonalityMessage(Ampersandposium);
      setPersonality(message);
    } catch (error) {
      console.error("Error fetching personality message:", error);
    }
  }, [Ampersandposium]); // Dependencies
  
  useEffect(() => {
    if (!wait) {
      // Ensure that the fetch is only attempted when all required values are available
      if (userEmail && Ampersandposium) {
        fetchPersonality();
      }
    }
  }, [wait, Ampersandposium]);
  
  
  // Update handleSiteUpload to accept siteUrl as a parameter
  async function handleSiteUpload(siteUrl) {
    setIsLoading(true);
    await vectorVault.addSite({ vault: Ampersandposium, site: siteUrl, name: siteUrl })
      .then(response => response.json())
      .then(data => {
        log(data);
      })
      .catch(error => {
        console.error("There was an error with the fetch operation:", error);
      })
      .finally(() => {
        setIsLoading(false);
        setReloadData(prevState => !prevState);
        alert("Site uploaded successfully.");
      });
  }




  document.addEventListener("touchstart", function() {
    if (document.body.style.zoom !== "1") {
      document.body.style.zoom = 1;
    }
  });

  return (
    <div style={styles.wrapper}>
      <div className="App" style={{ ...styles.body, paddingTop: "50px" }}>
        {/* <h1 style={{ ...styles.h1, textAlign: "left", fontSize: isMobile ? "1.5rem" : "2.9rem", display: "flex", alignItems: "left", marginTop: isMobile ? "50px" : "45px"}}>
          {Ampersandposium}
        </h1> */}
        <div style={{ marginTop: "20px" }}>
          {display && (
            <>
              {!showDelete && <div style={{ marginTop: "-40px" }}></div>}
              {showDelete && (
                <>
                  <div style={{ marginTop: "-10px" }}></div>
                  {database && (
                    <>
                      <div style={{ marginTop: '15px' }}></div>
                                         <VaultSelector
                       currentVault={Ampersandposium}
                       onVaultChange={setAmpersandposium}
                       userEmail={userEmail}
                       styles={styles}
                       isDarkMode={isDarkMode}
                       showDeleteButton={showVault}
                       showVaultLabel={true}
                       wait={wait}
                       showSearch={true}
                       vaults={vaults}
                       setVaults={setVaults}
                     />
                    {showVault &&
                      <>
                        <div style={{ marginTop: "20px" }}>
                          <AddToVaultSection 
                            isDarkMode={isDarkMode}
                            handleFileUpload={handleFileUpload}
                            handleSiteUpload={handleSiteUpload}
                            handleCustomDataSave={handleCustomDataSave}
                            isMobile={isMobile}
                            styles={styles}
                            setReloadData={setReloadData}
                          />
                        </div>
                      </>
                    }
                    </>
                  )}
                  <div className="loading-spinner" style={{ display: isLoading ? 'block' : 'none' }}>
                    <svg viewBox="0 0 50 50">
                      <circle cx="25" cy="25" r="20" stroke="#00d67a" strokeWidth="5" fill="none" />
                    </svg>
                  </div>
                  <div>
                    {customDataVisible && (
                      <>
                        <textarea
                          placeholder={`Type or paste something you want to add to the database...`}
                          id="chat-message"
                          rows={isMobile ? 15 : 10}
                          style={{ ...styles.textarea, marginTop: "5px", paddingRight: "6px", paddingLeft: "8px" }}
                          value={customData}
                          onChange={(e) => setCustomData(e.target.value)}
                        />
                        <button style={{ ...styles.button, marginTop: "0px", marginLeft: "0px" }} onClick={() => handleCustomDataSave(customData)}>Save to database</button>
                        <br />
                      </>
                    )}
                    <div style={{marginBottom: "-10px"}} />
                    {showPersonality &&
                    <>
                      <button className={isDarkMode ? 'btn-dark' : 'btn-light'} style={{ marginTop: "30px", marginLeft: "0px" }} onClick={() => setIsVisible(!isVisible)}>
                        {isVisible ? 'Hide' : 'Edit'} Personality
                      </button>
                      {isVisible && (
                        <>
                          <textarea
                            placeholder="No personality set, using base LLM personality"
                            id="chat-message"
                            rows={calculateRows(personality)}
                            style={{ 
                              ...styles.textarea, 
                              marginTop: "5px", 
                              paddingRight: "6px", 
                              paddingLeft: "8px"
                            }}
                            value={personality}
                            onChange={(e) => setPersonality(e.target.value)}
                          />
                          <button style={{ ...styles.button, marginTop: "0px", marginLeft: "1px" }} onClick={() => handlePersonalitySave(personality)}>Save Personality</button>
                        </>
                      )}
                    </>
                    }
                  </div>
                  <br />
                </>
              )}
            </>
          )}
        </div>
        {notificationMessage && (
          <div style={{ marginTop: "10px" }}>
            <Notification
              message={notificationMessage}
            />
          </div>
        )}
        <ChatContainer
          isDarkMode={isDarkMode}
          ref={ChatContainerRef}
          isMobile={isMobile}
          windowWidth={windowWidth}
          Ampersandposium={Ampersandposium}
          display={display}
          reloadData={reloadData}
          setReloadData={setReloadData}
          showDelete={showDelete}
          userEmail={userEmail}
          wait={wait}
          styles={styles}
          setNotificationMessage={setNotificationMessage}
          displayValue={displayValue}
          setDisplayValue={setDisplayValue}
        />
      </div>
    </div>
  );
}

export default Dashboard;



